import { z } from 'zod';

import { requestJson } from './requests';

const AI_MESSAGE_ROLES = ['guest', 'staff', 'assistant', 'system', 'tool'] as const;
const AI_VISIBILITY_VALUES = ['guest', 'staff', 'private'] as const;
const AI_SESSION_STATUS_VALUES = ['open', 'waiting_staff', 'escalated', 'resolved', 'archived'] as const;
const AI_SESSION_CHANNEL_VALUES = ['dashboard', 'sms', 'email', 'web', 'voice'] as const;
const AI_HANDOFF_TYPE_VALUES = ['assignment', 'escalation', 'notification', 'resolution'] as const;
const KNOWLEDGE_SOURCE_TYPE_VALUES = ['menu_item', 'modifier', 'ingredient', 'policy', 'faq', 'staff_doc', 'custom'] as const;

const AiStaffConversationMessageSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  role: z.enum(AI_MESSAGE_ROLES),
  visibility: z.enum(AI_VISIBILITY_VALUES),
  staffId: z.string().nullable(),
  content: z.string().nullable(),
  contentJson: z.record(z.unknown()).nullable().optional(),
  tokensIn: z.number().nullable().optional(),
  tokensOut: z.number().nullable().optional(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
});

export type AiStaffConversationMessage = z.infer<typeof AiStaffConversationMessageSchema>;

const AiStaffConversationSessionSchema = z.object({
  id: z.string().uuid(),
  locationId: z.string().uuid().nullable(),
  startedByStaffId: z.string().nullable(),
  assignedStaffId: z.string().nullable(),
  channel: z.enum(AI_SESSION_CHANNEL_VALUES),
  status: z.enum(AI_SESSION_STATUS_VALUES),
  visibility: z.enum(AI_VISIBILITY_VALUES),
  topic: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastMessageAt: z.string(),
});

export type AiStaffConversationSession = z.infer<typeof AiStaffConversationSessionSchema>;

const AiStaffQuotaEvaluationSchema = z.object({
  allowed: z.boolean(),
  status: z.enum(['disabled', 'ok', 'warning', 'critical', 'exhausted', 'unlimited']),
  quota: z.number().nullable(),
  usageChats: z.number(),
  projectedChats: z.number(),
  remainingChats: z.number().nullable(),
  threshold80: z.boolean(),
  threshold100: z.boolean(),
  shouldBackoff: z.boolean(),
  message: z.string().nullable().optional(),
});

export type AiStaffQuotaEvaluation = z.infer<typeof AiStaffQuotaEvaluationSchema>;

const AiStaffUsageMetricsSchema = z.object({
  tokensIn: z.number().nullable().optional(),
  tokensOut: z.number().nullable().optional(),
});

export type AiStaffUsageMetrics = z.infer<typeof AiStaffUsageMetricsSchema>;

const AiStaffQueryResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    conversationId: z.string().uuid(),
    response: z.string(),
    finishReason: z.string().nullable().optional(),
    quota: AiStaffQuotaEvaluationSchema,
    usage: AiStaffUsageMetricsSchema,
    messages: z.array(AiStaffConversationMessageSchema),
  }),
});

export type AiStaffQueryResponse = z.infer<typeof AiStaffQueryResponseSchema>['data'];

const AiStaffKnowledgeChunkSchema = z.object({
  id: z.string().uuid(),
  sourceType: z.enum(KNOWLEDGE_SOURCE_TYPE_VALUES),
  sourceId: z.string().nullable(),
  visibility: z.enum(AI_VISIBILITY_VALUES),
  locale: z.string(),
  title: z.string().nullable(),
  content: z.string(),
  metadata: z.record(z.unknown()),
  updatedAt: z.string(),
  createdAt: z.string(),
});

export type AiStaffKnowledgeChunk = z.infer<typeof AiStaffKnowledgeChunkSchema>;

const AiStaffKnowledgeResponseSchema = z.object({
  ok: z.literal(true),
  data: z.array(AiStaffKnowledgeChunkSchema),
});

const AiStaffSessionsResponseSchema = z.object({
  ok: z.literal(true),
  data: z.array(AiStaffConversationSessionSchema),
});

const AiStaffMessagesResponseSchema = z.object({
  ok: z.literal(true),
  data: z.array(AiStaffConversationMessageSchema),
});

const AiStaffReindexResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    inserted: z.number(),
    updated: z.number(),
    removed: z.number(),
    total: z.number(),
  }),
});

export type AiStaffReindexResult = z.infer<typeof AiStaffReindexResponseSchema>['data'];

const AiStaffSettingsSchema = z.object({
  enabled: z.boolean(),
  includedChats: z.number(),
  overageRateCentsPer1k: z.number(),
  smsPassThrough: z.boolean(),
  smsMarkupPct: z.number(),
  providerConfigured: z.boolean(),
});

const AiStaffSettingsResponseSchema = z.object({
  ok: z.literal(true),
  data: AiStaffSettingsSchema,
});

export type AiStaffSettings = z.infer<typeof AiStaffSettingsSchema>;

const AiStaffHandoffResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    id: z.string().uuid(),
    tenantId: z.string(),
    sessionId: z.string().uuid(),
    locationId: z.string().uuid().nullable(),
    staffId: z.string().nullable(),
    handoffType: z.enum(AI_HANDOFF_TYPE_VALUES),
    targetTicketId: z.string().nullable(),
    targetChannel: z.string().nullable(),
    notes: z.string().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string(),
  }),
});

export type AiStaffHandoffRecord = z.infer<typeof AiStaffHandoffResponseSchema>['data'];

type RequestSignal = { signal?: AbortSignal };

export type QueryAiStaffPayload = {
  conversationId?: string;
  locationId?: string;
  message: string;
  previewOnly?: boolean;
  temperature?: number;
  maxTokens?: number;
  locale?: string;
};

export async function queryAiStaff(
  payload: QueryAiStaffPayload,
  options: RequestSignal = {},
): Promise<AiStaffQueryResponse> {
  const response = await requestJson<z.infer<typeof AiStaffQueryResponseSchema>>('/ai-staff/query', {
    method: 'POST',
    body: payload,
    signal: options.signal,
    schema: AiStaffQueryResponseSchema,
  });
  return response.data;
}

export async function fetchAiStaffSessions(
  params: { limit?: number; status?: AiStaffConversationSession['status'] } = {},
  options: RequestSignal = {},
): Promise<AiStaffConversationSession[]> {
  const search = new URLSearchParams();

  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  if (params.status) {
    search.set('status', params.status);
  }

  const suffix = search.toString() ? `?${search.toString()}` : '';

  const payload = await requestJson<z.infer<typeof AiStaffSessionsResponseSchema>>(`/ai-staff/sessions${suffix}`, {
    signal: options.signal,
    schema: AiStaffSessionsResponseSchema,
  });

  return payload.data;
}

export async function fetchAiStaffMessages(
  sessionId: string,
  params: { limit?: number } = {},
  options: RequestSignal = {},
): Promise<AiStaffConversationMessage[]> {
  const search = new URLSearchParams();
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  const suffix = search.toString() ? `?${search.toString()}` : '';

  const payload = await requestJson<z.infer<typeof AiStaffMessagesResponseSchema>>(`/ai-staff/sessions/${sessionId}/messages${suffix}`, {
    signal: options.signal,
    schema: AiStaffMessagesResponseSchema,
  });
  return payload.data;
}

export async function fetchAiStaffKnowledge(
  params: { limit?: number; sourceType?: AiStaffKnowledgeChunk['sourceType']; visibility?: AiStaffKnowledgeChunk['visibility']; locale?: string } = {},
  options: RequestSignal = {},
): Promise<AiStaffKnowledgeChunk[]> {
  const search = new URLSearchParams();
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  if (params.sourceType) {
    search.set('sourceType', params.sourceType);
  }
  if (params.visibility) {
    search.set('visibility', params.visibility);
  }
  if (params.locale) {
    search.set('locale', params.locale);
  }
  const suffix = search.toString() ? `?${search.toString()}` : '';

  const payload = await requestJson<z.infer<typeof AiStaffKnowledgeResponseSchema>>(`/ai-staff/knowledge${suffix}`, {
    signal: options.signal,
    schema: AiStaffKnowledgeResponseSchema,
  });
  return payload.data;
}

export async function fetchAiStaffSettings(options: RequestSignal = {}): Promise<AiStaffSettings> {
  const response = await requestJson<z.infer<typeof AiStaffSettingsResponseSchema>>('/ai-staff/settings', {
    signal: options.signal,
    schema: AiStaffSettingsResponseSchema,
  });
  return response.data;
}

export type UpdateAiStaffSettingsPayload = Partial<
  Pick<AiStaffSettings, 'enabled' | 'includedChats' | 'overageRateCentsPer1k' | 'smsPassThrough' | 'smsMarkupPct'>
>;

export async function updateAiStaffSettings(
  payload: UpdateAiStaffSettingsPayload,
  options: RequestSignal = {},
): Promise<AiStaffSettings> {
  const response = await requestJson<z.infer<typeof AiStaffSettingsResponseSchema>>('/ai-staff/settings', {
    method: 'PATCH',
    body: payload,
    signal: options.signal,
    schema: AiStaffSettingsResponseSchema,
  });
  return response.data;
}

export async function triggerAiStaffReindex(
  input: { locationId?: string } = {},
  options: RequestSignal = {},
): Promise<AiStaffReindexResult> {
  const payload = await requestJson<z.infer<typeof AiStaffReindexResponseSchema>>('/ai-staff/reindex', {
    method: 'POST',
    body: input,
    signal: options.signal,
    schema: AiStaffReindexResponseSchema,
  });
  return payload.data;
}

export type RecordAiStaffHandoffPayload = {
  handoffType?: AiStaffHandoffRecord['handoffType'];
  targetTicketId?: string | null;
  targetChannel?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown>;
  staffId?: string | null;
  locationId?: string | null;
};

export async function recordAiStaffHandoff(
  sessionId: string,
  payload: RecordAiStaffHandoffPayload,
  options: RequestSignal = {},
): Promise<AiStaffHandoffRecord> {
  const body = {
    ...payload,
    handoffType: payload.handoffType ?? 'assignment',
  };

  const response = await requestJson<z.infer<typeof AiStaffHandoffResponseSchema>>(`/ai-staff/sessions/${sessionId}/handoff`, {
    method: 'POST',
    body,
    signal: options.signal,
    schema: AiStaffHandoffResponseSchema,
  });

  return response.data;
}

export {
  AiStaffConversationSessionSchema,
  AiStaffConversationMessageSchema,
  AiStaffKnowledgeChunkSchema,
};
